import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFirestore, collection, doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { onIdTokenChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { app, auth, functions } from '../firebase';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

const db = getFirestore(app);
const DEFAULT_TIMEOUT_SECONDS = 300;
const MIN_TIMEOUT_SECONDS = 30;
const MAX_TIMEOUT_SECONDS = 7200;

function serializeAlternativeNames(value) {
  if (typeof value !== 'string') {
    return [];
  }

  return value
    .split(/\r?\n/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function deserializeAlternativeNames(value) {
  if (!Array.isArray(value)) {
    return '';
  }

  return value
    .filter((entry) => typeof entry === 'string')
    .join('\n');
}

export default function ParticipantForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [status, setStatus] = useState('offline');
  const [linkedRoomIds, setLinkedRoomIds] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [callTimeoutSeconds, setCallTimeoutSeconds] = useState(String(DEFAULT_TIMEOUT_SECONDS));
  const [silenceDetectorEnabled, setSilenceDetectorEnabled] = useState(true);
  const [alternativeNames, setAlternativeNames] = useState('');

  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [adminDomainId, setAdminDomainId] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState('');

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setAuthChecked(true);
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        setIsSystemAdmin(tokenResult?.claims?.role === 'SYSTEM_ADMIN');
        setAdminDomainId(tokenResult?.claims?.domainId);
      } catch (e) {
        console.error(e);
      } finally {
        setAuthChecked(true);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!isSystemAdmin) return;
    const unsub = onSnapshot(collection(db, 'domains'), (snap) => {
      setDomains(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [isSystemAdmin]);

  useEffect(() => {
    if (!authChecked) return;
    const finalDomainId = isSystemAdmin ? domainId : adminDomainId;
    if (!finalDomainId) {
      setRooms([]);
      return;
    }

    const coll = collection(db, 'rooms');
    const q = query(coll, where('domainId', '==', finalDomainId));
    getDocs(q).then(s => {
      const list = [];
      s.forEach(d => list.push({ id: d.id, ...d.data() }));
      setRooms(list);
    }).catch(e => console.error('fetch rooms', e));
  }, [authChecked, isSystemAdmin, domainId, adminDomainId]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    const ref = doc(db, 'participants', id);
    getDoc(ref).then(snap => {
      if (!snap.exists()) {
        alert('Participant not found');
        navigate('/participants');
        return;
      }
      const data = snap.data();
      setName(data.name || '');
      setDomainId(data.domainId || '');
      setStatus(data.status || 'offline');
      setLinkedRoomIds(data.linkedRoomIds || []);
      setAlternativeNames(deserializeAlternativeNames(data.alternativeNames));
      const timeoutField = data.callTimeoutSeconds;
      if (typeof timeoutField === 'number' && Number.isFinite(timeoutField)) {
        setCallTimeoutSeconds(String(timeoutField));
      } else if (typeof timeoutField === 'string') {
        const parsed = parseInt(timeoutField, 10);
        setCallTimeoutSeconds(String(!Number.isNaN(parsed) ? parsed : DEFAULT_TIMEOUT_SECONDS));
      } else {
        setCallTimeoutSeconds(String(DEFAULT_TIMEOUT_SECONDS));
      }
      setSilenceDetectorEnabled(data.silenceDetectorEnabled !== false);
    }).catch(e => console.error('fetch participant', e)).finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!authChecked) return;

    const finalDomainId = isSystemAdmin ? domainId : adminDomainId;
    if (!finalDomainId) {
      alert('Please specify a domain.');
      return;
    }

    const payload = {
      name,
      domainId: finalDomainId,
      status,
      linkedRoomIds,
      alternativeNames: serializeAlternativeNames(alternativeNames),
    };
    const parsedTimeout = parseInt(callTimeoutSeconds, 10);
    const sanitizedTimeout = Number.isFinite(parsedTimeout)
      ? Math.max(MIN_TIMEOUT_SECONDS, Math.min(MAX_TIMEOUT_SECONDS, parsedTimeout))
      : DEFAULT_TIMEOUT_SECONDS;
    payload.callTimeoutSeconds = sanitizedTimeout;
    payload.silenceDetectorEnabled = silenceDetectorEnabled;
    try {
      if (isEdit) {
        const updateParticipant = httpsCallable(functions, 'updateParticipantAccount');
        await updateParticipant({
          participantId: id,
          ...payload,
        });
      } else {
        const createParticipant = httpsCallable(functions, 'createParticipantAccount');
        await createParticipant(payload);
      }
      navigate('/participants');
    } catch (e) {
      console.error('save participant', e);
      alert('Failed to save participant');
    }
  };

  if (loading || !authChecked) return <div>Loading...</div>;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5">{isEdit ? 'Edit Participant' : 'Create Participant'}</Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <TextField label="Name" value={name} onChange={e => setName(e.target.value)} required />
        <TextField
          label="Alternative Names"
          value={alternativeNames}
          onChange={e => setAlternativeNames(e.target.value)}
          multiline
          minRows={3}
          helperText="One alias per line. Used for future voice resolution only."
        />

        {isSystemAdmin && (
          <FormControl required>
            <InputLabel>Domain</InputLabel>
            <Select value={domainId} label="Domain" onChange={e => {
              setDomainId(e.target.value);
              setLinkedRoomIds([]); // Reset linked rooms when domain changes
            }}>
              {domains.map(d => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={e => setStatus(e.target.value)}>
            <MenuItem value="offline">offline</MenuItem>
            <MenuItem value="online">online</MenuItem>
            <MenuItem value="in-call">in-call</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Call Timeout (seconds)"
          type="number"
          inputProps={{ min: 30, max: 7200 }}
          value={callTimeoutSeconds}
          onChange={e => setCallTimeoutSeconds(e.target.value)}
          helperText="Maximum call duration before automatic hang up."
          required
        />
        <FormControlLabel
          control={
            <Switch
              checked={silenceDetectorEnabled}
              onChange={e => setSilenceDetectorEnabled(e.target.checked)}
            />
          }
          label="Silence detector enabled"
        />
        <FormControl disabled={!domainId && isSystemAdmin}>
          <InputLabel>Linked Rooms</InputLabel>
          <Select multiple value={linkedRoomIds} label="Linked Rooms" onChange={e => setLinkedRoomIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}>
            {rooms.map(r => <MenuItem key={r.id} value={r.id}>{r.name || r.id}</MenuItem>)}
          </Select>
        </FormControl>
        <Box>
          <Button type="submit" variant="contained">Save</Button>
          <Button type="button" variant="outlined" onClick={() => navigate('/participants')} sx={{ ml: 1 }}>Cancel</Button>
        </Box>
      </Box>
    </Box>
  );
}
